import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { filter } from 'rxjs';
import { NavigationStart } from '@angular/router';

// Composants
import { BaseComponent, CONVERSATION_QUERY_PARAM, SELECTED_QUERY_PARAM } from './base.component';

/**
 * Composant de base pour les dialogs qui doivent se fermer automatiquement lors d'une navigation
 *
 * Ce composant étend BaseComponent et ajoute la logique de fermeture automatique du dialog
 * lorsqu'une navigation est détectée (ce qui peut arriver depuis un composant enfant).
 */
@Component({
    selector: 'biifor-app-dialog-base',
    template: `
        NO UI TO BE FOUND HERE!
    `,
    standalone: false
})
export abstract class DialogBaseComponent extends BaseComponent implements OnInit {

    protected _dialogRef = inject(DynamicDialogRef, { optional: true });
    protected _dialogConfig = inject(DynamicDialogConfig, { optional: true });
    private _isCleaningUrl = false;

    /**
     * Cette fenêtre rend-elle l'adresse à la liste en se fermant (#995) ?
     *
     * **C'est celui qui ouvre la fenêtre qui décide, pas la fenêtre.** Une même fenêtre
     * s'ouvre depuis sa liste — l'adresse la désigne alors, et il faut nettoyer — comme depuis
     * un select à création rapide, le menu latéral, une carte ou l'échéancier d'un emprunt, où
     * elle ne désigne rien. `TableBaseComponent.openRowDetailDialog()` pose donc le drapeau
     * dans les données de la fenêtre ; tous les autres appelants n'ont rien à faire.
     *
     * ⚠️ **Nettoyer à tort ferme la fenêtre du dessous.** Le nettoyage navigue, la valeur de
     * `selected` change, et `_shouldCloseOnNavigation()` ferme la fenêtre qui l'avait ouverte
     * — la fenêtre d'une transaction bancaire disparaîtrait en fermant celle d'un achat à
     * débit différé qu'elle a ouverte.
     *
     * Redéfinir cette propriété reste possible pour une fenêtre qui saurait mieux, mais les
     * données passées à l'ouverture priment.
     */
    protected clearsSelectedOnClose: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    override ngOnInit() {
        super.ngOnInit();
        this._subscribeToNavigation();
        this._subscribeToClose();
    }

    /**
     * Rend à l'adresse la liste seule, à la fermeture de la fenêtre de détail (#995).
     *
     * Le fil de discussion part avec la ligne : il n'a pas de sens sans elle, et le laisser
     * rouvrirait le volet à la prochaine ouverture d'une autre ligne.
     *
     * En `replaceUrl` : l'ouverture a empilé une entrée d'historique, la fermeture la
     * remplace. Un retour arrière ramène ainsi à l'état d'avant l'ouverture, et non à la
     * fenêtre qu'on vient de fermer.
     */
    public removeSelectedFromUrl(): void {
        if (this._isCleaningUrl) {
            return;
        }
        this._isCleaningUrl = true;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [SELECTED_QUERY_PARAM]: null, [CONVERSATION_QUERY_PARAM]: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        }).finally(() => {
            this._isCleaningUrl = false;
        });
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /**
     * Décision effective, du plus général au plus précis : comportement de base (ne rien
     * nettoyer) → redéfinition éventuelle de la fenêtre → volonté de l'appelant, qui prime.
     */
    private get _clearsSelectedOnClose(): boolean {
        const requested = this._dialogConfig?.data?.clearsSelectedOnClose;
        return typeof requested === 'boolean' ? requested : this.clearsSelectedOnClose;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    /**
     * Méthode appelée après une fermeture demandée par l'utilisateur — bouton, échappement,
     * clic sur le masque — et **non** par une navigation.
     *
     * À redéfinir pour ce qui doit suivre la fermeture volontaire d'une fenêtre.
     */
    protected afterUserClose(): void {
        // Méthode vide par défaut, à surcharger si nécessaire
    }

    /**
     * Méthode appelée avant la fermeture du dialog lors d'une navigation
     * Peut être redéfinie dans les composants enfants pour effectuer des actions avant la fermeture
     */
    protected beforeNavigationClose(): void {
        // Méthode vide par défaut, à surcharger si nécessaire
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Chemin d'une URL, ses paramètres de requête retirés. Sert à distinguer un changement
     * d'écran d'une simple mise à jour des paramètres de la liste sous-jacente.
     */
    private _pathOf(url: string): string {
        const tree = this.router.parseUrl(url);
        tree.queryParams = {};
        tree.fragment = null;
        return this.router.serializeUrl(tree);
    }

    /**
     * Faut-il fermer la fenêtre pour cette navigation ?
     *
     * Oui dans deux cas seulement : on quitte l'écran, ou l'adresse désigne une **autre**
     * ligne — la fenêtre doit alors céder la place à celle de la nouvelle ligne.
     *
     * Non quand seuls les autres paramètres changent. Une liste réémet son tri, sa
     * pagination et ses filtres après le chargement de son état et à chaque chargement
     * paresseux : fermer là-dessus faisait clignoter la fenêtre ouverte depuis un lien
     * partagé, et relançait la lecture de la ligne (#995).
     *
     * `router.url` porte encore l'adresse courante à ce stade — elle n'est remplacée qu'à
     * la fin de la navigation.
     */
    private _shouldCloseOnNavigation(targetUrl: string): boolean {
        if (this._pathOf(this.router.url) !== this._pathOf(targetUrl)) return true;

        const current = this.router.parseUrl(this.router.url).queryParams[SELECTED_QUERY_PARAM];
        const target = this.router.parseUrl(targetUrl).queryParams[SELECTED_QUERY_PARAM];
        return current !== target;
    }

    /**
     * Souscription à la fermeture de la fenêtre.
     *
     * Une fermeture provoquée par une navigation ne touche à rien : c'est la navigation qui
     * décide de l'adresse, et elle a déjà eu lieu.
     *
     * Volontairement hors de `subscriptions` : la destruction du composant et l'émission de
     * `onClose` sont concurrentes, et une désinscription au `ngOnDestroy` pourrait manger
     * l'évènement qu'on attend.
     */
    private _subscribeToClose(): void {
        this._dialogRef?.onClose.subscribe((data: any) => {
            if (data?.reason === 'navigation') {
                return;
            }
            if (this._clearsSelectedOnClose) {
                this.removeSelectedFromUrl();
            }
            this.afterUserClose();
        });
    }

    /**
     * Souscription aux événements de navigation pour fermer le dialog automatiquement
     */
    private _subscribeToNavigation(): void {
        if (!this._dialogRef) {
            return;
        }

        this.subscriptions.add(
            this.router.events.pipe(
                filter(event => event instanceof NavigationStart)
            ).subscribe((event) => {
                if (!this._isCleaningUrl && this._shouldCloseOnNavigation((event as NavigationStart).url)) {
                    this.beforeNavigationClose();
                    this._dialogRef?.close({reason: 'navigation'});
                }
            })
        );
    }
}
